import Typeahead from '@components/typeahead/typeahead';
import { debounceTime, map } from "rxjs";
import { getArticlePageUrl,
  getArticleSuggestionsUrl, getArticleSearchResultPageUrl } from '../services/articleSearchBoxUrlService';

/**
 * Redirects the current window to the specified URL.
 * @param {string} url - The URL to redirect to.
 */
const redirectToUrl = (url) => window.open(url, '_self');

/**
 * Toggles the error state of a typeahead form based on the presence of results.
 *
 * This function selects a form element with the `data-article-search-box-form` attribute
 * and toggles the `nhs-c-typeahead__form--error` CSS class on it. The class is
 * added when there are no results (`hasResults` is `false`) and removed when
 * there are results (`hasResults` is `true`).
 * @param {boolean} hasResults - Indicates whether the typeahead search has results.
 */
const toggleTypeaheadFormErrorState = function(hasResults) {
  const form = document.querySelector('[data-article-search-box-form]');
  if (form) {
    form.classList.toggle('nhs-c-typeahead__form--error', !hasResults);
  }
};

/**
 * Retrieves the source URL for article suggestions based on the content client.
 * @returns {string} The URL for fetching article suggestions.
 */
const getSourceUrl = function () {
  const contentClientElement = document.querySelector('[data-article-search-box-content-client]');
  const contentClient = contentClientElement ? contentClientElement.dataset?.articleSearchBoxContentClient : '';
  return getArticleSuggestionsUrl(contentClient);
};

/**
 * Handles the event when a suggestion from the list is clicked.
 * This function extracts the article data from the clicked suggestion, 
 * generates the article URL using `getArticlePageUrl`, and redirects the user 
 * to the article page.
 * @param {HTMLElement} articleSuggestion - The clicked suggestion element.
 */
const suggestionClickedEvent = function(articleSuggestion) {
  const articleData = articleSuggestion.dataset;
  const articleUrl = getArticlePageUrl(articleData);
  redirectToUrl(articleUrl);
};

/**
 * Handles the event when the submit button is clicked.
 * This function retrieves the search term and the `fromPage` parameter, 
 * generates the search results page URL using `getSearchResultPageUrl`, 
 * and redirects the user to the search results page.
 * @param {string} searchTerm - The search term entered by the user.
 */
const submitButtonClickedEvent = function(searchTerm) {
  const fromPageSlugElement = document.querySelector('[data-from-page]');
  const contentClientElement = document.querySelector('[data-article-search-box-content-client]');
  if(fromPageSlugElement && contentClientElement) {
    const contentClient = contentClientElement.dataset?.articleSearchBoxContentClient;
    const fromPageParameter = fromPageSlugElement.dataset.fromPage;
    const articleSuggestionsUrl = getArticleSearchResultPageUrl(searchTerm, fromPageParameter, contentClient);
    redirectToUrl(articleSuggestionsUrl);
  }
};

/**
 * Initializes the Typeahead component with predefined settings.
 * The settings include query selectors for various elements in the search box, 
 * the source URL for fetching suggestions, and the key to extract suggestion values.
 */
const typeahead = new Typeahead();
typeahead.setTypeaheadSetting({
  searchInputQuerySelector: '[data-article-search-box]',
  suggestionsListQuerySelector: '[data-article-search-box-suggestions]',
  errorContainerQuerySelector: '[data-article-search-box-error]',
  clearButtonQuerySelector: '[data-article-search-box-clear]',
  submitButtonQuerySelector: '[data-article-search-box-submit]',
  sourceUrl: getSourceUrl(),
  suggestionValueKey: 'Title'
});

/**
 * Observable for handling clicks on the suggestion list.
 * This observable listens for click events on the suggestion list, waits for 
 * 100ms using `debounceTime` to prevent rapid consecutive clicks, and then
 * calls `suggestionListClickedEvent` to handle the click.
 */
const suggestionListClicked$ = typeahead.getSuggestionListClickedObserver().pipe(debounceTime(100));
suggestionListClicked$.subscribe(suggestionClickedEvent);

/**
 * Observable for handling the submit button click event.
 * This observable listens for click events on the submit button, waits for 
 * 100ms using `debounceTime` to prevent rapid consecutive clicks, and then 
 * calls `submitButtonClickedEvent` to handle the click.
 */
const submitEventObserver$ = typeahead.getSubmitEventObserver().pipe(debounceTime(100));
submitEventObserver$.subscribe(submitButtonClickedEvent);

/**
 * Observable that emits a boolean indicating whether the typeahead search results contain any items.
 * 
 * @constant {Observable<boolean>} hasSearchResults$
 */
const hasSearchResults$ = typeahead.getSearchResultObserver().pipe(
  map((list) => list.length > 0),
);
hasSearchResults$.subscribe(toggleTypeaheadFormErrorState);

export default typeahead;